/**
 * return_to deep-link convention (eReg source-of-truth plan §3.6).
 *
 * A `return_to` query param must be a SAME-ORIGIN RELATIVE path — anything
 * else (absolute URLs, protocol-relative //host, backslash tricks, control
 * chars) is dropped. Callers navigate with react-router, so a sanitized value
 * can never leave the SPA.
 *
 * ⚠️ No backend endpoint consumes return_to today, so this client-side
 * sanitizer is the only validation. ANY future server-side consumer (emails,
 * redirects, the P5 unified inbox) MUST mirror these rules before
 * interpolating the param — otherwise it reopens the open-redirect vector
 * this file exists to close (plan §3.6: "validated server- and client-side").
 */

export function sanitizeReturnTo(raw: string | null | undefined): string | null {
  if (!raw || typeof raw !== 'string') return null;
  // Must be a rooted relative path: "/x" yes; "//host", "\", schemes, no.
  if (!raw.startsWith('/') || raw.startsWith('//')) return null;
  if (raw.includes('\\')) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001F]/.test(raw)) return null;
  return raw;
}

/** Append a query param to a relative path that may already carry a query. */
export function appendQueryParam(path: string, key: string, value: string): string {
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
}

/**
 * Build the canonical eReg deep links (§3.6). All params optional; return_to
 * is sanitized here so callers can pass location.pathname+search verbatim.
 */
export function eregStudyCreateLink(opts: {
  protocolNumber?: string;
  sponsor?: string;
  nickname?: string;
  returnTo?: string | null;
  returnLabel?: string;
} = {}): string {
  const params = new URLSearchParams();
  if (opts.protocolNumber) params.set('protocol_number', opts.protocolNumber);
  if (opts.sponsor) params.set('sponsor', opts.sponsor);
  if (opts.nickname) params.set('nickname', opts.nickname);
  const rt = sanitizeReturnTo(opts.returnTo);
  if (rt) params.set('return_to', rt);
  if (rt && opts.returnLabel) params.set('return_label', opts.returnLabel);
  const qs = params.toString();
  return `/ereg/studies/new${qs ? `?${qs}` : ''}`;
}

export function eregStudyLink(protocolId: string, opts: { returnTo?: string | null; returnLabel?: string } = {}): string {
  const params = new URLSearchParams();
  const rt = sanitizeReturnTo(opts.returnTo);
  if (rt) params.set('return_to', rt);
  if (rt && opts.returnLabel) params.set('return_label', opts.returnLabel);
  const qs = params.toString();
  return `/ereg/studies/${protocolId}${qs ? `?${qs}` : ''}`;
}

export function eregStudyUploadLink(protocolId: string, opts: { role?: string; returnTo?: string | null; returnLabel?: string } = {}): string {
  const params = new URLSearchParams();
  if (opts.role) params.set('role', opts.role);
  const rt = sanitizeReturnTo(opts.returnTo);
  if (rt) params.set('return_to', rt);
  if (rt && opts.returnLabel) params.set('return_label', opts.returnLabel);
  const qs = params.toString();
  return `/ereg/studies/${protocolId}/upload${qs ? `?${qs}` : ''}`;
}
